import {useTranslation} from "react-i18next";
import {InfoWindow, InfoWindowProps} from "../InfoWindow/InfoWindow";
import {InfoWindowBody} from "../InfoWindowBody/InfoWindowBody";
import {Badge as MantineBadge, Box, Divider, Group, Stack, Text, ThemeIcon} from "@mantine/core";
import {IconMapPin} from "@tabler/icons-react";

import {FormattedDateTime} from "../../../date/FormattedDateTime/FormattedDateTime";
import {OpsLocationStatus} from "../../../../model/ops-status";
import React, {useMemo} from "react";
import {
  getRefServiceStatusColorRef,
  getRefServiceStatusDisplayValue
} from "../../../../util/ref-service-status-ui-util";
import {EntityAnchor} from "../../../anchor/EntityAnchor/EntityAnchor";
import {Badge} from "../../../badge/Badge/Badge";

export type OpsStatusInfoWindowProps = Omit<InfoWindowProps, 'children'> & {
  ctx: OpsLocationStatus
}

export const OpsStatusInfoWindow = (props: OpsStatusInfoWindowProps) => {
  const {ctx, options, ...rest} = props;

  const {t} = useTranslation("common");

  const serviceTypes = useMemo(() => {
    return Object.values(ctx.serviceTypes);
  }, [ctx.serviceTypes]);

  const lastAsOfDttm = useMemo(() => {
    let max: number | undefined = undefined;

    for (const st of serviceTypes) {
      for (const facet of Object.values(st.facets)) {
        for (const m of facet.modes) {
          if (max === undefined || m.asOfDttm > max) {
            max = m.asOfDttm;
          }
        }
      }
    }

    return max;
  }, [serviceTypes]);

  return (
    <InfoWindow
      {...rest}
      options={{
        ...options,
        pixelOffset: options?.pixelOffset ?? new google.maps.Size(0, -30)
      }}>
      <InfoWindowBody>
        <Box style={{maxWidth: 260}}>
          <Stack spacing={4}>
            {/* Header */}
            <Group spacing="xs" noWrap>
              <ThemeIcon variant="light" c="blue" radius="xl" size="xs">
                <IconMapPin size={14}/>
              </ThemeIcon>
              <Text fz="sm" fw={500} lineClamp={2}>
                <EntityAnchor
                  value={{
                    objectId: ctx.siteId,
                    objectName: ctx.siteName,
                    objectType: "SITE",
                  }}
                />
              </Text>
            </Group>

            {/* Overall status */}

            <Group spacing="xs" my={8}>
              <MantineBadge
                size="xs"
                variant="light"
                c={getRefServiceStatusColorRef(ctx.overallStatus)}
              >
                {getRefServiceStatusDisplayValue(ctx.overallStatus, t)}
              </MantineBadge>
              {lastAsOfDttm && (
                <Text fz="xs" c="dimmed">
                  {t("common.map.infowindow.ctxops.lastUpdate", "Updated")}{" "}
                  <FormattedDateTime date={lastAsOfDttm}/>
                </Text>
              )}
            </Group>

            <Divider my={4} variant="dashed"/>

            {/* Service type summary */}
            {serviceTypes.map((st) => (
              <Group spacing="xs" mt={4} key={st.serviceTypeId}>
                <Text fz="xs" c="dimmed">
                  {st.serviceTypeName}:
                </Text>
                <Badge
                  variant="light"
                  c={getRefServiceStatusColorRef(st.overallStatus)}
                  size="sm"
                >
                  {getRefServiceStatusDisplayValue(st.overallStatus, t)}
                </Badge>
              </Group>
            ))}
          </Stack>
        </Box>
      </InfoWindowBody>
    </InfoWindow>
  )
};
